import * as React from 'react'
import {Component, ReactElement, ReactNode} from 'react'
import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import styled from 'styled-components'
import {Colors, FontFamily, FontSize, FontWeights} from '../../../../styledComponents/Constants'
import {createIconWithLevel} from '../../../../styledComponents/adminPage/IconCreator'
import {FullWidthLeftRowFlexContainer, LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {MESSAGE_TYPE} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import {UnstyledLinkStyledButton} from '../../../../styledComponents/Button'
import {HideInPC, HideInSP} from '../../../../styledComponents/ResponsiveConvenience'

export interface DialogButtonParams {
    buttonLabel: string
    buttonHandler: (...params: any) => void
    isDefault: boolean
    color?: Colors
}

export interface DialogParams {
    title: string
    titleIcon?: ReactNode
    errorLevel: MESSAGE_TYPE
    message: string
    buttonProps: DialogButtonParams[]
}

interface Props {
    params: Nullable<DialogParams>
    open: boolean
}

export default class NatResidentMessageDialogComponent extends Component<Props> {

    constructor(props: Props) {
        super(props)
    }

    render(): Nullable<ReactElement> {
        if (!this.props.params) {
            return null
        }

        const getTitleIcon = this.props.params.titleIcon ?
            this.props.params.titleIcon : createIconWithLevel(this.props.params.errorLevel)

        return (
            <>
                <HideInSP>
                    <Dialog
                        open={this.props.open}
                        aria-labelledby="dialog-title"
                        aria-describedby="dialog-description"
                        transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                    >
                        <PCDialogTitle id="dialog-title">
                            <BoldLeftRowFlexContainer style={{fontSize: '20px'}}>
                                {getTitleIcon}{this.props.params.title}
                            </BoldLeftRowFlexContainer>
                        </PCDialogTitle>
                        <PCDialogContent>
                            <PCDialogContentText id="dialog-description">
                                {this.props.params.message}
                            </PCDialogContentText>
                        </PCDialogContent>
                        <DialogActions>
                            {
                                this.props.params.buttonProps.map((buttonProp, index) => {
                                    const ButtonElement =
                                        <PCDialogButton
                                            color={'inherit'}
                                            onClick={buttonProp.buttonHandler}
                                        >
                                            {buttonProp.buttonLabel}
                                        </PCDialogButton>
                                    return (
                                        <Box key={index} color={buttonProp.color ?? Colors.Primary}>
                                            {ButtonElement}
                                        </Box>
                                    )
                                })
                            }
                        </DialogActions>
                    </Dialog>
                </HideInSP>
                <HideInPC>
                    <SPMessageDialog
                        open={this.props.open}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                        transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                    >
                        <SPDialogTitle
                            id="alert-dialog-title"
                            sx={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {getTitleIcon}{this.props.params.title}
                        </SPDialogTitle>
                        <SPDialogContent>
                            <SPDialogContentText id="alert-dialog-description">
                                {this.props.params.message}
                            </SPDialogContentText>
                        </SPDialogContent>
                        <SPDialogActions>
                            <FullWidthLeftRowFlexContainer>
                                {
                                    this.props.params.buttonProps.map((buttonProp, index) => (
                                        <SPDialogActionButton
                                            key={index}
                                            buttonColor={buttonProp.color}
                                            onClick={buttonProp.buttonHandler}
                                        >
                                            {buttonProp.buttonLabel}
                                        </SPDialogActionButton>
                                    ))
                                }
                            </FullWidthLeftRowFlexContainer>
                        </SPDialogActions>
                    </SPMessageDialog>
                </HideInPC>
            </>
        )
    }
}

const SPMessageDialog = withStyles({
    paper: {
        maxWidth: '420px',
        minWidth: '290px',
    },
})(Dialog)

const PCDialogTitle = styled(DialogTitle)`
  &.MuiDialogTitle-root > h2 {
    font-size: ${FontSize.Accent};
    color: ${Colors.Body};
  }
`

const PCDialogContent = styled(DialogContent)`
  &.MuiDialogContent-root {
    overflow-y: visible;
  }
`

const PCDialogContentText = styled(DialogContentText)`
  &.MuiDialogContentText-root {
    font-size: ${FontSize.Body};
    color: ${Colors.Body};
    white-space: pre-line;
  }
`

const PCDialogButton = styled(Button)`
  &.MuiButton-text {
    font-size: ${FontSize.Middle};
  }
`

const BoldLeftRowFlexContainer = styled(LeftRowFlexContainer)`
  font-weight: ${FontWeights.Bold};

  svg {
    font-size: ${FontSize.Accent};
    margin-right: 2px;
  }
`

const SPDialogActions = styled(DialogActions)`
  &.MuiDialogActions-root {
    padding: 0;
  }
`

const SPDialogTitle = styled(DialogTitle)<{ size?: string, weight?: number }>`
    &.MuiDialogTitle-root.MuiTypography-h6 {
    padding: 15px 15px 0 15px;
    color: ${Colors.Body};
    font-size: 16px;
    font-weight: ${FontWeights.Bold};
    font-family: ${FontFamily.ResidentDefault};
    display: flex;
    align-items: center;
  }

  &.MuiTypography-h6 svg {
    font-size: 20px;
    margin-right: 10px;
  }
`

const SPDialogContent = styled(DialogContent)`
  &.MuiDialogContent-root {
    padding: 10px 15px 20px 15px;
  }

  & .MuiTypography-root {
    color: ${Colors.Body};
    font-size: 14px;
    font-family: ${FontFamily.ResidentDefault};
  }
`

const SPDialogContentText = styled(DialogContentText)`
  &.MuiDialogContentText-root {
    font-size: ${FontSize.Middle};
    color: ${Colors.Body};
    white-space: pre-line;
    margin-bottom: 0;
  }
`

const SPDialogActionButton = styled(UnstyledLinkStyledButton)<{ buttonColor?: Colors }>`
  margin: 0;
  padding: 10px 10px 15px;
  width: 100%;
  border-top: ${Colors.AccentLight} solid 1px;
  white-space: nowrap;
  color: ${props => props.buttonColor ? props.buttonColor : Colors.Primary};
  font-size: ${FontSize.Body};

  &:not(:last-child) {
    border-right: ${Colors.AccentLight} solid 1px;
  }
`
